import {
  GrowthBook,
  GrowthBookProvider as GrowthBookProviderBase,
} from "@growthbook/growthbook-react";
import { autoAttributesPlugin } from "@growthbook/growthbook/plugins";
import { trackSelfDescribingEvent } from "@snowplow/browser-tracker";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { Loader } from "@features/ui";
import { useAuth } from "@hooks/use-auth";
import { isGrowthbookEnabled } from "@lib/app-features.helpers";
import { SP_GROWTHBOOK_TRACKER } from "@src/observability";

// Create a GrowthBook instance
const gb = new GrowthBook({
  apiHost: import.meta.env.GROWTHBOOK_API_HOST,
  clientKey: import.meta.env.GROWTHBOOK_CLIENT_KEY,
  enableDevMode: import.meta.env.DEV,
  // Only required for A/B testing
  // Called every time a user is put into an experiment
  trackingCallback: (experiment, result) => {
    console.log("Experiment Viewed", {
      experimentId: experiment.key,
      variationId: result.key,
      hashAttribute: result.hashAttribute,
      hashValue: result.hashValue,
    });
    trackSelfDescribingEvent(
      {
        event: {
          schema: "iglu:io.growthbook/experiment_viewed/jsonschema/1-0-0",
          data: {
            experimentId: experiment.key,
            variationId: result.key,
            hashAttribute: result.hashAttribute,
            hashValue: result.hashValue,
          },
        },
      },
      [SP_GROWTHBOOK_TRACKER],
    );
  },
  plugins: [autoAttributesPlugin({ uuidKey: "autoId" })],
});
gb.init({ streaming: true });

export const GrowthBookProvider = ({ children }) => {
  const [ready, setReady] = useState(false);

  const { user } = useAuth();
  const growthbookEnabled = isGrowthbookEnabled();

  useEffect(() => {
    // Clear attributes when the component unmounts
    return () => {
      gb.setAttributes({});
    };
  }, []);

  useEffect(() => {
    if (!growthbookEnabled || !user) {
      // Clear attributes if growthbook is not enabled or user is not logged in
      gb.setAttributes({});
      setReady(true);
      return;
    }

    // Safety net to make sure the children are not rendered before the feature
    // flags are configured for the user
    setReady(false);

    // Update attributes with user data
    gb.setAttributes({
      ...user,
      id: user.userId,
      organizationId: user.orgCode,
    }).then(() => {
      setReady(true);
    });
  }, [user]);

  return (
    <GrowthBookProviderBase growthbook={gb}>
      {ready ? children : <Loader show />}
    </GrowthBookProviderBase>
  );
};

GrowthBookProvider.propTypes = {
  children: PropTypes.node,
};
